/**
 * Shared signal cells for the Portfolio + Alpha Zone holdings tables
 * (2026-05-22 Steve directive). Renders +ve / -ve / = / — for the four
 * qualitative signals consumed from /api/holdings-signals:
 *
 *   Cong   — Congressional buy vs sell (last 90d)
 *   Insid  — SEC Form 4 net-buying (last 6mo aggregate)
 *   Call   — Latest earnings-call tone
 *   Earn   — Earnings momentum (epsRevision + recent surprise)
 *
 * Each cell is click-to-open the StockStoryDrawer (parent passes onSelect).
 * The drawer surfaces the full underlying detail; signal cells are the
 * at-a-glance reads.
 */
'use client';

type Signal = 'pos' | 'neg' | 'flat' | 'none';
type CallTone = 'positive' | 'neutral' | 'cautious' | 'negative' | 'none';

export interface SymbolSignals {
  symbol: string;
  cong: Signal;
  congDetail: { buy90d: number; sell90d: number; total90d: number };
  insider: Signal;
  insiderDetail: { netBuyRatio: number | null; form4Count: number };
  call: Signal;
  callTone: CallTone;
  callPeriod: string | null;
  earn: Signal;
  earnDetail: { epsRevision: number | null; earningsSurprisePct: number | null };
}

export interface HoldingsSignalsResp {
  asOf: string;
  count: number;
  signals: SymbolSignals[];
}

const STYLE: Record<Signal, { glyph: string; cls: string }> = {
  pos:  { glyph: '+ve', cls: 'text-emerald-300' },
  neg:  { glyph: '-ve', cls: 'text-red-300' },
  flat: { glyph: '=',   cls: 'text-amber-300/70' },
  none: { glyph: '—',   cls: 'text-[#475569]' },
};

export type SignalCellType = 'cong' | 'insider' | 'call' | 'earn';

export function SignalCell({
  signal,
  title,
  onClick,
}: {
  signal: Signal;
  title: string;
  /** Called with no args so the parent can decide whether to open the small
   *  per-signal popover or the full StockStoryDrawer. Stops row-click. */
  onClick: () => void;
}) {
  const { glyph, cls } = STYLE[signal];
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      className={`col-span-1 text-center text-[10px] tabular-nums ${cls} hover:underline cursor-pointer`}
    >
      {glyph}
    </button>
  );
}

export function buildCellTitles(s: SymbolSignals | undefined): {
  cong: string;
  insider: string;
  call: string;
  earn: string;
} {
  if (!s) {
    return {
      cong:    'Congressional activity (last 90d) — no data',
      insider: 'Insider buy/sell ratio — no data',
      call:    'Latest earnings-call tone — no transcript yet',
      earn:    'Earnings momentum — no data',
    };
  }
  const c = s.congDetail;
  const i = s.insiderDetail;
  const e = s.earnDetail;
  const congTxt =
    s.cong === 'none'
      ? 'No Congressional trades in last 90d'
      : `${c.buy90d} buy / ${c.sell90d} sell in last 90d — click for detail`;
  const insTxt =
    s.insider === 'none'
      ? 'No insider activity on file'
      : `Net-buy ratio ${i.netBuyRatio != null ? i.netBuyRatio.toFixed(2) : '—'} across ${i.form4Count} Form 4 filings — click for detail`;
  const callTxt =
    s.call === 'none'
      ? 'No earnings-call summary yet'
      : `Latest call tone: ${s.callTone}${s.callPeriod ? ` (${s.callPeriod})` : ''} — click for detail`;
  const earnTxt =
    s.earn === 'none'
      ? 'No analyst revisions or recent surprise on file'
      : `epsRevision z=${e.epsRevision != null ? e.epsRevision.toFixed(2) : '—'}` +
        (e.earningsSurprisePct != null ? `, last surprise ${(e.earningsSurprisePct * 100).toFixed(1)}%` : '') +
        ' — click for detail';
  return { cong: congTxt, insider: insTxt, call: callTxt, earn: earnTxt };
}
